import { useFetchApi } from "./useFetchApi";
import { Language } from "../utils/localization/languages/types";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { customLocationValuesFromSearch } from "src/utils/localization/geolocation/geolocationLoader";
import { match } from "ts-pattern";
import { useQuerySearch } from "src/utils/useQueryParams";
import { isPresent } from "src/utils/checks";

type Expiration = { days: number; date: Date };

type CourtesyHoldEnabled = {
  type: "CourtesyHoldEnabled";
  cruiseCode: string;
  expiration: Expiration;
};

type CourtesyHoldDisabled = {
  type: "CourtesyHoldDisabled";
  cruiseCode: string;
};

type CourtesyHoldResponse =
  | Readonly<CourtesyHoldEnabled>
  | Readonly<CourtesyHoldDisabled>;

const apiUrlFor = (
  cruiseCode: string,
  country: string | undefined,
  language: Language
): string => {
  const baseUrl =
    process.env.GATSBY_ECOMMERCE_BASE_API_URL || "http://localhost:3000/dev/";
  const apiUrl = new URL(`${cruiseCode}/courtesyHold`, baseUrl);

  apiUrl.searchParams.append("locale", language.code);
  if (country) apiUrl.searchParams.append("country", country);
  return apiUrl.href;
};

type Enabled = {
  type: "enabled";
  expiration: Expiration;
};

type Disabled = {
  type: "disabled";
};

type Loading = {
  type: "loading";
};

type ECommerceStatus = Loading | Enabled | Disabled;

export const useECommerceCourtesyHoldStatus = (
  cruiseCode: string,
  isCruiseWaitlist: boolean
): ECommerceStatus => {
  const language = useLanguage();
  const searchParams = useQuerySearch();

  const params = !isPresent(searchParams)
    ? {}
    : customLocationValuesFromSearch(searchParams);
  const overriddenCountryCode = params.countryCode;

  const country = overriddenCountryCode ? overriddenCountryCode : undefined;
  const apiUrl = apiUrlFor(cruiseCode, country, language);

  const { status, data, isLoading } = useFetchApi<CourtesyHoldResponse>(
    isCruiseWaitlist ? undefined : apiUrl,
    ["ecommerce-courtesy-hold-is-enabled", cruiseCode]
  );

  if (isCruiseWaitlist) return { type: "disabled" as const };
  if (isLoading) return { type: "loading" };

  if (status == "success" && data) {
    return match(data)
      .with({ type: "CourtesyHoldEnabled" }, ({ expiration }) => ({
        expiration,
        type: "enabled" as const
      }))
      .with({ type: "CourtesyHoldDisabled" }, () => ({
        type: "disabled" as const
      }))
      .exhaustive();
  }

  return { type: "disabled" };
};
